import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Select, Spin } from 'antd';
import PhoneInput from 'react-phone-number-input';
import React from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import TextArea from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';

import {
  FormAttachmentProps,
  FormButtonProps,
  FormCheckboxProps,
  FormDatePickerProps,
  FormFieldProps,
  FormNumberProps,
  FormPhoneNumberProps,
  FormSelectProps,
  FormTextAreaProps,
  FormTitleProps,
} from '../../types/FormFieldTypes';
import FormDragDropUpload from './FormDragDropUpload';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { DateValidationEnum } from '../../enums/DateValidationEnum';
import RESOURCE_CONFIGS from '../../constants/resourceConfig';
import { phoneNumberValidator } from '../../helpers/formHelper';

const MAX_SAFE_FLOAT = 9007199254740991.91;

export const getRules = (
  t: any,
  isRequired: boolean | undefined,
  rules: any,
  type?: FormFieldTypeEnum,
  maxLength?: number
) => {
  const validationRules = [];

  const hasCustomValidator =
    Array.isArray(rules) && rules.some((r) => typeof r?.validator === 'function');

  const hasRequiredRule =
    Array.isArray(rules) && rules.some((r) => r?.required === true);

  if (isRequired && !hasCustomValidator && !hasRequiredRule) {
    validationRules.push({
      required: true,
      message: t('required'),
      ...(type === FormFieldTypeEnum.INPUT && { whitespace: true }),
    });
  }

  if (maxLength) {
    validationRules.push({
      validator: (_: any, value: any) => {
        if (value && value.length > maxLength) {
          return Promise.reject(t('maxCharacters', { max: maxLength }));
        }
        return Promise.resolve();
      },
      validateTrigger: ['onChange'],
    });
  }

  return rules ? [...validationRules, ...rules] : validationRules;
};

export const FormButton = ({
  label,
  type,
  icon,
  isIconOnly,
  disabled,
  onClick,
  isLoading,
  className,
}: FormButtonProps) => (
  <Button
    onClick={onClick}
    className={isIconOnly ? 'btn-primary-icon-only' : className ? className : 'btn-primary'}
    type="primary"
    htmlType={type}
    icon={icon}
    disabled={disabled}
    loading={isLoading}
  >
    {label}
  </Button>
);

export const FormField = ({
  name,
  label,
  rules,
  placeholder,
  disabled,
  type = FormFieldTypeEnum.INPUT,
  isRequired = false,
  allowLeadingTrailingSpaces = true,
  maxLength,
  minLength,
  useCustomRules = false,
  ...rest
}: FormFieldProps) => {
  const { t } = useTranslation('form');
  const ruleToApply = useCustomRules ? rules : getRules(t, isRequired, rules, type, maxLength);
  return (
    <Form.Item
      name={name}
      label={label}
      required={isRequired}
      validateFirst
      rules={ruleToApply}
      normalize={(value) => (allowLeadingTrailingSpaces ? value : value?.trim())}
    >
      <Input
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength ? maxLength + 1 : undefined}
        minLength={minLength ? minLength : undefined}
        {...rest}
      />
    </Form.Item>
  );
};

export const FormTextArea = ({
  name,
  label,
  rules,
  placeholder,
  disabled,
  isRequired = false,
  maxLength,
  useCustomRules = false,
  ...rest
}: FormTextAreaProps) => {
  const { t } = useTranslation('form');
  const ruleToApply = useCustomRules
    ? rules
    : getRules(t, isRequired, rules, FormFieldTypeEnum.TEXT_AREA, maxLength);
  return (
    <Form.Item name={name} label={label} validateFirst rules={ruleToApply}>
      <TextArea
        placeholder={placeholder}
        disabled={disabled}
        rows={4}
        maxLength={maxLength ? maxLength + 1 : undefined}
        {...rest}
      />
    </Form.Item>
  );
};

export const FormSelect = ({
  name,
  options,
  label,
  rules,
  disabled,
  placeholder,
  isRequired,
  onChange,
  allowClear,
  showSearch,
  loading,
  ...rest
}: FormSelectProps) => {
  const { t } = useTranslation('form');
  return (
    <Form.Item name={name} label={label} rules={getRules(t, isRequired, rules)}>
      <Select
        disabled={disabled || loading}
        onChange={onChange}
        placeholder={loading ? t('loadingData') : placeholder}
        options={options}
        allowClear={allowClear}
        loading={loading}
        notFoundContent={
          loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px' }}>
              <Spin size="small" />
              <span>{t('loadingData')}</span>
            </div>
          ) : (
            t('noData')
          )
        }
        showSearch={showSearch}
        {...rest}
      ></Select>
    </Form.Item>
  );
};

export const FormTitle = ({ label, styles }: FormTitleProps) => (
  <p className={`${styles ? styles : 'mt-10 text-xl font-semibold text-primary'}`}>{label}</p>
);

export const FormLabel = ({ label }: FormTitleProps) => (
  <p className="text-1xl text-black text-label-black">{label}</p>
);

export const FormPhoneNumber = ({
  label,
  name,
  rules,
  disabled,
  defaultCountry = ENVIRONMENT_CONFIG.COUNTRY_CODE,
  isRequired,
}: FormPhoneNumberProps) => {
  const { t } = useTranslation('form');
  const mergedRules = [...(rules || []), { validator: phoneNumberValidator(t) }];
  return (
    <Form.Item
      name={name}
      label={label}
      required={isRequired}
      rules={getRules(t, isRequired, mergedRules)}
    >
      <PhoneInput
        international
        defaultCountry={defaultCountry}
        countryCallingCodeEditable={false}
        onChange={() => {}}
        disabled={disabled}
      />
    </Form.Item>
  );
};

export const FormDatePicker = ({
  label,
  rules,
  showTime = false,
  maxDate,
  disabled,
  isRequired,
  name,
  format,
  selectFutureDatesOnly = false,
}: FormDatePickerProps) => {
  const { t } = useTranslation('form');
  const resolvedMaxDate =
    maxDate === DateValidationEnum.CURRENT_DATE
      ? dayjs()
      : typeof maxDate === 'number'
        ? dayjs(maxDate)
        : undefined;

  const disabledDate = (current: dayjs.Dayjs) => {
    if (!resolvedMaxDate) return false;
    return current.isAfter(resolvedMaxDate, showTime ? 'minute' : 'day');
  };

  const disabledDatesBeforeCurrent = (current: dayjs.Dayjs) => {
    dayjs.extend(isSameOrBefore);
    return current.isSameOrBefore(dayjs(), showTime ? 'minute' : 'day');
  };

  return (
    <Form.Item name={name} label={label} rules={getRules(t, isRequired, rules)}>
      <DatePicker
        className="w-full"
        showTime={showTime}
        showSecond={false}
        disabled={disabled}
        disabledDate={selectFutureDatesOnly ? disabledDatesBeforeCurrent : disabledDate}
        format={format != null ? format : RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT}
      ></DatePicker>
    </Form.Item>
  );
};

export const FormAttachment = ({
  name = 'files',
  label,
  isRequired,
  form,
  maxCount,
  fileTypes,
  initialFiles,
  isDisabled,
  validationMessage,
  cameraOnly,
  description,
}: FormAttachmentProps) => {
  const { t } = useTranslation('form');
  let formRules: any = [];
  if (isRequired) {
    formRules = [
      { required: true, message: '' },
      () => ({
        validator(_: any, value: any) {
          if (!value || value.length === 0) {
            return Promise.reject(
              new Error(`${validationMessage ? validationMessage : t('required')} `)
            );
          }
          return Promise.resolve();
        },
      }),
    ];
  }

  const namePath = Array.isArray(name) ? name : [name];
  const lastKey = namePath[namePath.length - 1];

  return (
    <Form.Item name={name} label={label} rules={formRules} valuePropName="value">
      <p className="text-sm text-gray-500">{description}</p>
      <FormDragDropUpload
        maxCount={maxCount || 1}
        name={[lastKey]}
        form={form}
        fileTypes={fileTypes}
        initialFiles={initialFiles}
        isDisabled={isDisabled}
        cameraOnly={cameraOnly}
      />
    </Form.Item>
  );
};

export const FormCheckbox = ({ id, label, rules, name, disabled, onChange }: FormCheckboxProps) => {
  return (
    <Form.Item name={name} rules={rules} valuePropName="checked">
      <Checkbox id={id} disabled={disabled} onChange={onChange}>
        {label}
      </Checkbox>
    </Form.Item>
  );
};

export const FormNumber = ({
  name,
  label,
  tooltip,
  rules,
  placeholder,
  isRequired = false,
  addonAfter,
  disabled,
  onChange,
  max,
  min,
  type = 'integer',
  precision,
}: FormNumberProps) => {
  const { t } = useTranslation('form');

  // Create default validator based on type
  const defaultRules = [];
  if (type === 'float' && !max) {
    defaultRules.push({
      validator: (_: any, value: any) => {
        if (!value) return Promise.resolve();
        if (value > MAX_SAFE_FLOAT) {
          return Promise.reject(new Error(t('invalidInput')));
        }
        return Promise.resolve();
      },
    });
  }

  // Get base rules (including required) from user-provided rules only
  const formRules = getRules(t, isRequired, rules);

  // Combine all rules: base + default
  const allRules = [...formRules, ...defaultRules];

  return (
    <Form.Item name={name} label={label} tooltip={tooltip} rules={allRules}>
      <InputNumber
        onChange={onChange ? onChange : null}
        className="w-full"
        placeholder={placeholder}
        addonAfter={addonAfter}
        onWheel={(e) => e.currentTarget.blur()}
        max={max}
        min={min}
        disabled={disabled}
        precision={precision}
        step={type === 'float' ? 0.01 : 1}
        onKeyDown={(e) => {
          if (
            [
              'Backspace',
              'Delete',
              'Tab',
              'Escape',
              'Enter',
              '.',
              'ArrowLeft',
              'ArrowRight',
            ].includes(e.key)
          ) {
            return;
          }
          if (e.ctrlKey || e.metaKey) {
            return;
          }
          if (!/^[0-9]$/.test(e.key)) {
            e.preventDefault();
          }
        }}
      />
    </Form.Item>
  );
};
